/**
 * PDP hero: when `data-bm-hero-handles` lists product handles, syncs the right column via section fetch.
 * Single-product media carousels use an empty handle list so only pagination scroll behaviour runs.
 */
(function () {
  'use strict';

  /**
   * Same pattern as product-info.js: full HTML + section_id (not ?sections=main JSON, which can be { main: null }
   * when the product’s assigned template has no `main` key or Section Rendering omits it).
   */
  function productSectionHtmlUrl(handle, sectionId) {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var root = r.endsWith('/') ? r : r + '/';
    return (
      root +
      'products/' +
      encodeURIComponent(handle) +
      '?section_id=' +
      encodeURIComponent(sectionId)
    );
  }

  function handleFromProductUrl(url) {
    if (!url || typeof url !== 'string') return '';
    var pathOnly = url.split('?')[0];
    var parts = pathOnly.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  }

  function parseHandles(root) {
    var tag = root.querySelector('script[type="application/json"][data-bm-hero-handles]');
    if (!tag || !tag.textContent) return [];
    try {
      var arr = JSON.parse(tag.textContent.trim());
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Long bar pagination sits in a horizontal scrollport (`.swiper-controls`, overflow-x: auto).
   * Centre the active bullet by adjusting **only** that element's scrollLeft.
   * Do not use scrollIntoView on the bullet — it scrolls the window when the hero is off-screen,
   * which pulls users away from the buy box at the bottom of long PDPs on every slide change.
   */
  function slideshowIntersectsViewport(slideshowEl) {
    var r = slideshowEl.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return r.bottom > 0 && r.top < vh;
  }

  function scrollPdpHeroPaginationIntoView(slideshowEl) {
    if (!slideshowEl || typeof slideshowEl.querySelector !== 'function') return;
    if (!slideshowIntersectsViewport(slideshowEl)) return;
    try {
      var wrap = slideshowEl.querySelector('.swiper-controls');
      if (!wrap) return;
      var active = wrap.querySelector('.swiper-pagination-bullet.swiper-pagination-bullet-active');
      if (!active) return;

      var wrapRect = wrap.getBoundingClientRect();
      var activeRect = active.getBoundingClientRect();
      var delta = activeRect.left + activeRect.width / 2 - (wrapRect.left + wrapRect.width / 2);
      var maxLeft = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
      var nextLeft = Math.max(0, Math.min(wrap.scrollLeft + delta, maxLeft));

      /* Instant scroll only — avoids smooth-scroll quirks on nested scrollports. */
      wrap.scrollLeft = nextLeft;
    } catch (e) {
      /* ignore */
    }
  }

  function scheduleScrollPdpHeroPagination(slideshowEl) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        scrollPdpHeroPaginationIntoView(slideshowEl);
      });
    });
  }

  function parsePositiveInt(value, fallback) {
    var n = parseInt(value, 10);
    if (Number.isNaN(n) || n <= 0) return fallback;
    return n;
  }

  /**
   * Pagination scroll-into-view + optional per-slide autoplay delay (longer on 3D model slides).
   */
  function bindHeroSliderChrome(root, slideshow) {
    function schedulePagination() {
      scheduleScrollPdpHeroPagination(slideshow);
    }

    var mo = new MutationObserver(function () {
      schedulePagination();
    });
    mo.observe(slideshow, { attributes: true, attributeFilter: ['selected-index'] });

    var defaultMs = parsePositiveInt(root.dataset.bmHeroAutoplayDefaultMs, 0);
    var modelMs = parsePositiveInt(root.dataset.bmHeroAutoplayModelMs, 0);
    var useVariableAutoplay = defaultMs > 0 && modelMs > 0;

    function delayMsForActiveSlide(slider) {
      if (!useVariableAutoplay) return defaultMs;
      var slideEl = slider.slides[slider.activeIndex];
      if (!slideEl) return defaultMs;
      var mt = slideEl.getAttribute('data-media-type');
      return mt === 'model' ? modelMs : defaultMs;
    }

    function applyAutoplayDelayForActiveSlide(slider) {
      if (!useVariableAutoplay || !slider || !slider.params || !slider.autoplay) return;
      if (typeof slider.params.autoplay !== 'object') return;
      var nextDelay = delayMsForActiveSlide(slider);
      if (typeof nextDelay !== 'number' || nextDelay <= 0) return;
      slider.params.autoplay.delay = nextDelay;
      slider.params.autoplay.disableOnInteraction = false;
      slideshow.style.setProperty('--swiper-autoplay-delay', nextDelay + 1000 + 'ms');
      try {
        if (typeof slider.autoplay.stop === 'function') slider.autoplay.stop();
        if (typeof slider.autoplay.start === 'function') slider.autoplay.start();
      } catch (err) {
        console.error('[bm-hero-slider] autoplay delay update failed', err);
      }
    }

    var bindAttempts = 0;
    var maxBindAttempts = 240;

    function bindSwiperEvents() {
      var inst = slideshow.sliderInstance;
      if (!inst || !inst.slider) {
        bindAttempts += 1;
        if (bindAttempts < maxBindAttempts) {
          window.requestAnimationFrame(bindSwiperEvents);
        }
        return;
      }
      var slider = inst.slider;
      function onSlide() {
        schedulePagination();
        applyAutoplayDelayForActiveSlide(slider);
      }
      slider.on('slideChange', onSlide);
      slider.on('realIndexChange', onSlide);
      applyAutoplayDelayForActiveSlide(slider);
      window.setTimeout(schedulePagination, 120);
    }

    bindSwiperEvents();
    window.setTimeout(schedulePagination, 0);
    window.setTimeout(schedulePagination, 200);
    window.setTimeout(schedulePagination, 600);
  }

  function initBmPdpHero(root) {
    var slideshow = root.querySelector('#bm-pdp-hero-slideshow') || root.querySelector('slideshow-component');
    if (!slideshow) return;

    bindHeroSliderChrome(root, slideshow);

    var handles = parseHandles(root);
    if (!handles.length) {
      return;
    }

    var productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) {
      return;
    }

    var fetchAbort = null;
    /* Match the column already rendered (URL product), not handles[0], so the first fetch can correct drift */
    var activeHandle = handleFromProductUrl(productInfo.dataset.url) || handles[0] || '';

    function reinitProductInfoScripts() {
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        try {
          window.Shopify.PaymentButton.init();
        } catch (e) {
          /* ignore */
        }
      }
      var vs = productInfo.querySelector('variant-selects');
      var checked = vs && vs.querySelector('input[type="radio"]:checked');
      if (checked) {
        checked.dispatchEvent(new Event('change', { bubbles: true }));
      }
      document.dispatchEvent(
        new CustomEvent('bm-hero:product-info-swapped', {
          detail: { handle: activeHandle },
        })
      );
    }

    function fetchProductInfo(handle) {
      if (!handle || handle === activeHandle) return;
      var sectionId = productInfo.dataset.section;
      if (!sectionId) {
        console.error('[bm-hero-slider] Missing product-info data-section; cannot fetch section HTML.');
        return;
      }
      if (fetchAbort) fetchAbort.abort();
      fetchAbort = new AbortController();

      fetch(productSectionHtmlUrl(handle, sectionId), { signal: fetchAbort.signal })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          fetchAbort = null;
          if (!html || typeof html !== 'string') return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var srcPi = doc.querySelector('product-info[id^="MainProduct-"]');
          if (!srcPi) {
            console.error('[bm-hero-slider] No product-info in fetched HTML for handle:', handle);
            return;
          }

          var srcInfo = srcPi.querySelector('.product__info-container[id^="ProductInfo-"]');
          var destInfo = productInfo.querySelector('.product__info-container[id^="ProductInfo-"]');
          if (!srcInfo || !destInfo) return;

          destInfo.innerHTML = srcInfo.innerHTML;

          if (srcPi.dataset.productId != null) {
            productInfo.dataset.productId = srcPi.dataset.productId;
          }
          if (srcPi.dataset.url != null) {
            productInfo.dataset.url = srcPi.dataset.url;
          }

          activeHandle = handle;
          reinitProductInfoScripts();
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') {
            console.error('[bm-hero-slider] fetch failed', err);
          }
        });
    }

    var debounceApply = null;

    function applySlideIndex(idx) {
      if (typeof idx !== 'number' || Number.isNaN(idx) || idx < 0 || idx >= handles.length) return;
      var h = handles[idx];
      if (h) fetchProductInfo(h);
    }

    function scheduleApplyFromAttribute() {
      if (debounceApply) clearTimeout(debounceApply);
      debounceApply = setTimeout(function () {
        debounceApply = null;
        var raw = slideshow.getAttribute('selected-index');
        var idx = raw === null || raw === '' ? 0 : parseInt(raw, 10);
        if (Number.isNaN(idx)) idx = 0;
        applySlideIndex(idx);
      }, 40);
    }

    /* slideshow-component sets selected-index in handleSlideChange (Swiper realIndex) */
    var mo = new MutationObserver(function () {
      scheduleApplyFromAttribute();
    });
    mo.observe(slideshow, { attributes: true, attributeFilter: ['selected-index'] });

    var bindAttempts = 0;
    var maxBindAttempts = 240;

    function bindSwiperEvents() {
      var inst = slideshow.sliderInstance;
      if (!inst || !inst.slider) {
        bindAttempts += 1;
        if (bindAttempts < maxBindAttempts) {
          window.requestAnimationFrame(bindSwiperEvents);
        }
        return;
      }
      var slider = inst.slider;
      function onSlide(sw) {
        var idx = typeof sw.realIndex === 'number' ? sw.realIndex : 0;
        if (debounceApply) clearTimeout(debounceApply);
        debounceApply = setTimeout(function () {
          debounceApply = null;
          applySlideIndex(idx);
        }, 40);
      }
      slider.on('slideChange', onSlide);
      slider.on('realIndexChange', onSlide);
      var ri = typeof slider.realIndex === 'number' ? slider.realIndex : 0;
      applySlideIndex(ri);
    }

    bindSwiperEvents();

    /* Swiper / theme may set selected-index late; align column to visible slide if it drifted */
    setTimeout(scheduleApplyFromAttribute, 0);
    setTimeout(scheduleApplyFromAttribute, 200);
    setTimeout(scheduleApplyFromAttribute, 600);
  }

  function boot() {
    document.querySelectorAll('[data-bm-hero-slider]').forEach(initBmPdpHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
